/**
 * AddNoteDialogWithSelector — top-level Add Note dialog with a meal-time
 * selector. Used from the page header.
 */
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useAddMealNote } from "@/hooks/useNutritionEvaluation";
import type { MealTime } from "@/types/nutrition";
import { cn } from "@/lib/utils";
import { Loader2, StickyNote } from "lucide-react";

import { MEAL_TIME_DISPLAY } from "./meal-constants";

const NOTE_MAX_LENGTH = 150;

interface AddNoteDialogWithSelectorProps {
  open: boolean;
  onClose: () => void;
  mealTimes: MealTime[];
  appointmentId: string;
  isTubeFeeding: boolean;
  isMealPlan?: boolean;
}

export const AddNoteDialogWithSelector: React.FC<
  AddNoteDialogWithSelectorProps
> = ({
  open,
  onClose,
  mealTimes,
  appointmentId,
  isTubeFeeding,
  isMealPlan = false,
}) => {
  const [note, setNote] = React.useState("");
  const [selectedMealGroupId, setSelectedMealGroupId] = React.useState<string>(
    mealTimes[0]?.id || ""
  );

  const addNote = useAddMealNote(appointmentId, isTubeFeeding, isMealPlan);

  React.useEffect(() => {
    if (mealTimes.length > 0 && !selectedMealGroupId) {
      setSelectedMealGroupId(mealTimes[0].id);
    }
  }, [mealTimes, selectedMealGroupId]);

  const handleAdd = () => {
    if (note.trim() && selectedMealGroupId) {
      addNote.mutate({ group_id: selectedMealGroupId, comment: note });
      onClose();
      setNote("");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        onClose();
        setNote("");
      }}
    >
      <DialogContent className="sm:max-w-[700px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="flex items-center gap-2 z-h2 text-foreground">
            <StickyNote className="h-5 w-5 text-primary" />
            Add Notes
          </DialogTitle>
        </DialogHeader>
        <Separator className="mt-4" />

        <div className="flex-1 overflow-y-auto p-6 bg-background space-y-4">
          <div className="flex items-center gap-4">
            <span className="z-h3 text-foreground">Food Group:</span>
            <Select
              value={selectedMealGroupId}
              onValueChange={setSelectedMealGroupId}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select meal time" />
              </SelectTrigger>
              <SelectContent>
                {mealTimes.map((mealTime) => (
                  <SelectItem key={mealTime.id} value={mealTime.id}>
                    {MEAL_TIME_DISPLAY[mealTime.meal_time] ||
                      mealTime.meal_time}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Textarea
            placeholder="Add Note..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={5}
            className="resize-none"
          />
          <p
            className={cn(
              "z-caption text-right",
              note.length > NOTE_MAX_LENGTH
                ? "text-destructive font-medium"
                : "text-muted-foreground"
            )}
          >
            {NOTE_MAX_LENGTH - note.length} characters left
          </p>
        </div>

        <DialogFooter className="p-6 mt-auto border-t border-border/60 bg-background flex justify-end gap-2">
          <Button
            onClick={handleAdd}
            disabled={
              !note.trim() ||
              !selectedMealGroupId ||
              note.length > NOTE_MAX_LENGTH ||
              addNote.isPending
            }
          >
            {addNote.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Add Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
